import { useTranslation } from "react-i18next";
import { formatPrice, type Locale } from "@nh/shared";
import { numberLocaleOf } from "@/features/shop/shop.catalog";
import type { CartSubtotal } from "../cart.pricing";

export interface CartSummaryProps {
  subtotal: CartSubtotal;
  locale: Locale;
}

/**
 * El subtotal del carrito.
 *
 * ── Dice "subtotal", no "total" ──────────────────────────────────────
 * Envío e impuestos no se calculan todavía —el checkout no existe— y
 * llamarle "total" a una cifra a la que le van a sumar cosas es la clase
 * de mentira que acaba en una reclamación. La nota de debajo lo dice con
 * todas las letras.
 *
 * ── El formato lo hace `formatPrice` ─────────────────────────────────
 * Una sola función sabe que los precios son centavos enteros. Dividir
 * entre 100 aquí es como se acaba con dos redondeos distintos en dos
 * pantallas y un subtotal que no cuadra con la suma de las filas.
 *
 * ── Los artículos que quedaron fuera se dicen ────────────────────────
 * Un producto que solo se vende en la otra moneda no se puede sumar a
 * este total sin inventar una conversión. Se avisa; callarlo dejaría un
 * subtotal más bajo de lo que la persona ve en las filas de arriba, y esa
 * diferencia se descubre en el peor momento.
 */
export function CartSummary({ subtotal, locale }: CartSummaryProps) {
  const { t } = useTranslation();
  const numberLocale = numberLocaleOf(locale);

  return (
    <div className="grid gap-2">
      <dl className="flex items-baseline justify-between gap-4">
        <dt className="text-[12px] font-semibold uppercase tracking-ds-label text-ds-ink-3">
          {t("Subtotal")}
        </dt>
        <dd className="text-[22px] font-light tracking-tight text-ds-ink">
          {formatPrice(subtotal.cents, subtotal.currency, numberLocale)}
        </dd>
      </dl>

      <p className="text-[11px] leading-relaxed text-ds-ink-3">
        {t("El envío y los impuestos se calculan al finalizar la compra.")}
      </p>

      {subtotal.skippedItems > 0 && (
        <p className="text-[11px] leading-relaxed text-ds-terracotta">
          {t("{{n}} artículos no se pueden sumar en {{moneda}}.", {
            n: subtotal.skippedItems,
            moneda: subtotal.currency,
          })}
        </p>
      )}
    </div>
  );
}
